import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import type { TargetAndTransition, Transition } from "framer-motion";
import {
  Wand2,
  Sparkles,
  Plus,
  HelpCircle,
  Info,
  Check,
  X,
  ShieldCheck,
  History,
  Pin,
  PinOff,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
// Utilisation de crypto.randomUUID() pour générer des IDs uniques

import DashboardLayout from "../../components/layout/DashboardLayout";
import DocumentType from "../../components/chat/DocumentType";
import ChatBubble from "../../components/chat/ChatBubble";
import FileAttachment from "../../components/chat/FileAttachment";
import ChatComposer from "../../components/chat/ChatComposer";
import GuidedPromptOptions from "../../components/chat/GuidedPromptOptions";
import ToastContainer from "../../components/ui/ToastContainer";
import { useToast } from "../../hooks/useToast";
import { contentGenerationService } from "../../services/api/contentGeneration";
import { ConversationState, ContentGenerationMessage } from "../../types/ContentGeneration";
import "../../styles/background-effects.css";

// Types pour l'historique
interface Conversation {
  id: string;
  title: string;
  messages: ContentGenerationMessage[];
  createdAt: Date;
  isPinned: boolean;
  docType?: string;
  docSubtype?: string;
  isGuided?: boolean; // Nouveau champ pour distinguer les conversations guidées
  chatInput?: string; // Nouveau champ pour stocker le texte de saisie par conversation
}


/* ───────────────────────── helpers ───────────────────────── */

/** Easing curves compatible with Framer Motion types */
const EASE_OUT: Transition["ease"] = [0.16, 1, 0.3, 1];
const EASE_IN: Transition["ease"] = [0.4, 0, 1, 1];

/** Base fade-up for enter/exit */
const fadeUp = (delay = 0): {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  exit: TargetAndTransition;
} => ({
  initial: { opacity: 0, y: 12 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { type: "tween", duration: 0.32, ease: EASE_OUT, delay },
  },
  exit: {
    opacity: 0,
    y: 12,
    transition: { type: "tween", duration: 0.2, ease: EASE_IN },
  },
});

/** Scroll-reveal (uses viewport) */
const fadeUpInView = (delay = 0): {
  initial: TargetAndTransition;
  whileInView: TargetAndTransition;
  viewport: { once: boolean; amount: number };
} => ({
  initial: { opacity: 0, y: 18, filter: "blur(2px)" },
  whileInView: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { type: "tween", duration: 0.38, ease: EASE_OUT, delay },
  },
  viewport: { once: false, amount: 0.25 },
});

/** Micro-interaction presets */
const hoverElevate: TargetAndTransition = { y: -1, boxShadow: "0 10px 24px rgba(0,0,0,0.06)" };
const tapPress: TargetAndTransition = { scale: 0.98 };

/** Stagger container for lists */
const staggerList = {
  initial: {},
  animate: { transition: { staggerChildren: 0.06, delayChildren: 0.02 } },
};

/* ────────────────────── Segmented Control ───────────────────── */
type TabKey = "manual" | "guided";

const Segmented: React.FC<{
  value: TabKey;
  onChange: (v: TabKey) => void;
}> = ({ value, onChange }) => {
  const order: TabKey[] = ["manual", "guided"];
  const tabs: Array<{ key: TabKey; label: string; icon: React.ReactNode }> = [
    { key: "manual", label: "Prompt manuel", icon: <Sparkles className="w-4 h-4" aria-hidden /> },
    { key: "guided", label: "Guidé", icon: <Wand2 className="w-4 h-4" aria-hidden /> },
  ];

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    const i = order.indexOf(value);
    if (e.key === "ArrowRight") onChange(order[(i + 1) % order.length]);
    else if (e.key === "ArrowLeft") onChange(order[(i - 1 + order.length) % order.length]);
    else if (e.key === "Home") onChange(order[0]);
    else if (e.key === "End") onChange(order[order.length - 1]);
  };

  return (
    <div
      role="tablist"
      aria-label="Mode de génération"
      className="relative inline-grid grid-cols-2 gap-1 p-1 bg-gray-100/80 border border-gray-200 rounded-xl"
      onKeyDown={onKeyDown}
    >
      {tabs.map((t) => {
        const active = value === t.key;
        return (
          <motion.button
            key={t.key}
            id={`tab-${t.key}`}
            role="tab"
            aria-selected={active}
            aria-controls={`panel-${t.key}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(t.key)}
            whileHover={hoverElevate}
            whileTap={tapPress}
            className={`relative isolate px-3 md:px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 transition-colors ${
              active ? "text-gray-900" : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {active && (
              <motion.span
                layoutId="seg-indicator"
                className="absolute inset-0 rounded-lg bg-white shadow-sm"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
                aria-hidden
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-2">
              {t.icon}
              {t.label}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
};

/* ───────────────────── Main Page Component ──────────────────── */
const RedactionPage: React.FC = () => {
  const { showSuccess, showError, toasts, removeToast } = useToast();
  const prefersReducedMotion = useReducedMotion();

  // Mode d'affichage
  const [tab, setTab] = useState<TabKey>("manual");
  
  // Flag pour distinguer les prompts du mode Guidé
  const [isGuidedPrompt, setIsGuidedPrompt] = useState(false);
  
  // État pour le prompt généré par le mode guidé
  const [guidedPrompt, setGuidedPrompt] = useState<string>("");

  // Debug: Log tab changes
  useEffect(() => {
    console.log('Tab changed to:', tab);
  }, [tab]);

  // Handle tab change with forced re-render
  const handleTabChange = (newTab: TabKey) => {
    console.log('Changing tab from', tab, 'to', newTab);
    // Force a small delay to ensure clean transition
    setTimeout(() => {
      setTab(newTab);
    }, 50);
  };

  // Chat state
  const [messages, setMessages] = useState<ContentGenerationMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  
  // État de génération de contenu
  const [conversationState, setConversationState] = useState<ConversationState>({
    step: 'initial',
    sessionId: null,
    originalPrompt: '',
    suggestedPrompt: '',
    generatedContent: '',
    documentType: '',
    documentSubtype: '',
    canDownload: false,
    isGenerating: false,
    error: null
  });

  // Historique des conversations
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>("");

  // Métadonnées document
  const [category, setCategory] = useState<string>("");
  const [subType, setSubType] = useState<string>("");
  const [documentType, setDocumentType] = useState("");
  const [documentSubtypeId, setDocumentSubtypeId] = useState<number | undefined>(undefined);
  const [modelAvailable, setModelAvailable] = useState<boolean>(true); // Par défaut, on assume que le modèle est disponible

  // Guided prompt
  const [objective, setObjective] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [sections, setSections] = useState<string[]>([]);
  const [tone, setTone] = useState("");

  // Fichiers & favoris
  const [attachments, setAttachments] = useState<File[]>([]);

  // Scroll auto + live region a11y
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth" });
  }, [messages, sending, prefersReducedMotion]);
  
  // Sauvegarder automatiquement le champ de saisie quand il change
  useEffect(() => {
    if (currentConversationId && chatInput !== undefined) {
      const timeoutId = setTimeout(() => {
        setConversations(prev => 
          prev.map(conv => 
            conv.id === currentConversationId 
              ? { ...conv, chatInput: chatInput }
              : conv
          )
        );
      }, 500); // Délai de 500ms pour éviter trop de sauvegardes
      
      return () => clearTimeout(timeoutId);
    }
  }, [chatInput, currentConversationId]);

  // Parallax for header blob
  const { scrollYProgress } = useScroll();
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const blobScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  /* ─────────────────────── logic helpers ────────────────────── */
  const resetChat = () => {
    setMessages([]);
    setChatInput("");
    setAttachments([]);
    setModelAvailable(true); // Réinitialiser la disponibilité du modèle
    setConversationState({
      step: 'initial',
      sessionId: null,
      originalPrompt: '',
      suggestedPrompt: '',
      generatedContent: '',
      documentType: '',
      documentSubtype: '',
      canDownload: false,
      isGenerating: false,
      error: null
    });
  };

  const startNewConversation = (isGuided: boolean = false) => {
    // Sauvegarder la conversation actuelle si elle contient des messages
    if (messages.length > 0 && currentConversationId) {
      const currentConversation: Conversation = {
        id: currentConversationId,
        title: messages[0]?.content?.substring(0, 50) + "..." || "Conversation",
        messages: messages,
        createdAt: new Date(),
        isPinned: false,
        docType: conversationState.documentType,
        docSubtype: conversationState.documentSubtype
      };
      
      // Mettre à jour la conversation existante dans l'historique
      setConversations(prev => 
        prev.map(conv => 
          conv.id === currentConversationId ? currentConversation : conv
        )
      );
    }
    
    // Créer une nouvelle conversation immédiatement visible
    const newId = crypto.randomUUID();
    const newConversation: Conversation = {
      id: newId,
      title: isGuided ? "Nouvelle conversation guidée" : "Nouvelle conversation",
      messages: [],
      createdAt: new Date(),
      isPinned: false,
      docType: conversationState.documentType || documentType,
      docSubtype: conversationState.documentSubtype || subType,
      isGuided: isGuided,
      chatInput: "", // Initialiser le champ de saisie vide
    };
    
    // Réinitialiser l'interface d'abord
    resetChat();
    
    // Réinitialiser le flag pour les prompts guidés
    setIsGuidedPrompt(false);
    
    // Réinitialiser le prompt guidé
    setGuidedPrompt("");
    
    // Ajouter la nouvelle conversation à l'historique
    setConversations(prev => {
      const updated = [newConversation, ...prev];
      console.log('📝 Nouvelle conversation ajoutée:', newConversation);
      console.log('📋 Total conversations:', updated.length);
      return updated;
    });
    setCurrentConversationId(newId);
    
    showSuccess("Nouveau chat créé.");
    setTab("manual");
  };

  const ensureConversation = (): string => {
    if (!currentConversationId) {
      const newId = crypto.randomUUID();
      const newConversation: Conversation = {
        id: newId,
        title: "Nouvelle conversation",
        messages: [],
        createdAt: new Date(),
        isPinned: false,
        docType: conversationState.documentType || documentType,
        docSubtype: conversationState.documentSubtype || subType,
        isGuided: false, // Par défaut, les conversations manuelles ne sont pas guidées
        chatInput: "", // Initialiser le champ de saisie vide
      };
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(newId);
      return newId;
    }
    return currentConversationId;
  };

  const saveCurrentConversation = () => {
    if (messages.length > 0 && currentConversationId) {
      const currentConversation: Conversation = {
        id: currentConversationId,
        title: messages[0]?.content?.substring(0, 50) + "..." || "Conversation",
        messages: messages,
        createdAt: new Date(),
        isPinned: false,
        docType: conversationState.documentType || documentType,
        docSubtype: conversationState.documentSubtype || subType,
        isGuided: isGuidedPrompt, // Utiliser le flag actuel pour déterminer le type
        chatInput: chatInput, // Sauvegarder le contenu du champ de saisie
      };
      
      setConversations(prev => 
        prev.map(conv => 
          conv.id === currentConversationId ? currentConversation : conv
        )
      );
    }
  };

  const startEditingTitle = (conversationId: string, currentTitle: string) => {
    setEditingTitleId(conversationId);
    setEditingTitle(currentTitle);
  };

  const saveTitleEdit = (conversationId: string) => {
    if (editingTitle.trim()) {
      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, title: editingTitle.trim() }
            : conv
        )
      );
    }
    setEditingTitleId(null);
    setEditingTitle("");
  };

  const cancelTitleEdit = () => {
    setEditingTitleId(null);
    setEditingTitle("");
  };

  const togglePin = (conversationId: string) => {
    setPinnedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conversationId)) {
        newSet.delete(conversationId);
      } else {
        newSet.add(conversationId);
      }
      return newSet;
    });
  };

  const deleteConversation = (conversationId: string) => {
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    if (currentConversationId === conversationId) {
      setCurrentConversationId(null);
      resetChat();
    }
  };

  const buildGuidedPrompt = () => {
    const lines: string[] = [];
    if (documentType) lines.push(`Type de document: ${documentType}.`);
    if (objective) lines.push(`Objectif: ${objective}.`);
    if (targetAudience) lines.push(`Public cible: ${targetAudience}.`);
    if (tone) lines.push(`Ton souhaité: ${tone}.`);
    if (sections.length) lines.push(`Structure attendue: ${sections.join(", ")}.`);
    lines.push("Contraintes: Rédige un premier jet clair, structuré avec titres/sous-titres.");
    return lines.join("\n");
  };

  const handleGenerateFromGuided = () => {
    if (!documentType) {
      showError("Sélectionnez un type de document.");
      return;
    }
    const prompt = buildGuidedPrompt();
    
    // Créer une nouvelle conversation au lieu de réinitialiser
    startNewConversation(true); // Passer true pour indiquer que c'est une conversation guidée
    
    // Marquer comme prompt guidé pour éviter l'amélioration automatique
    setIsGuidedPrompt(true);
    
    setTab("manual");
    
    // Sauvegarder le prompt dans la conversation et dans l'état local
    setTimeout(() => {
      if (currentConversationId) {
        setConversations(prev => 
          prev.map(conv => 
            conv.id === currentConversationId 
              ? { ...conv, chatInput: prompt }
              : conv
          )
        );
        
        // Mettre à jour l'état local du prompt guidé
        setGuidedPrompt(prompt);
        
        // Mettre à jour le champ de saisie seulement si c'est la conversation active
        setChatInput(prompt);
      }
    }, 100);
    
    showSuccess("Nouvelle conversation guidée créée avec le prompt généré.");
  };


  const handleSendMessage = async () => {
    const text = chatInput.trim();
    if (!text && attachments.length === 0) return;

    if (!documentType) {
      showError("Sélectionnez d'abord le type de document.");
      return;
    }

    const filesLabel = attachments.length ? `\n[Pièces jointes]: ${attachments.map((f) => f.name).join(", ")}` : "";
    const fullPrompt = `${text}${filesLabel}`;

    // Ajouter le message utilisateur
    const userMsg: ContentGenerationMessage = { 
      id: crypto.randomUUID(),
      role: "user", 
      content: fullPrompt,
      timestamp: new Date(),
      type: 'prompt'
    };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setChatInput("");
    setAttachments([]);

    // Assurer qu'une conversation existe
    ensureConversation();
    
  // Sauvegarder la conversation après chaque message
  setTimeout(() => saveCurrentConversation(), 100);

    try {
      setSending(true);
      setConversationState(prev => ({ ...prev, isGenerating: true, error: null }));

      // Vérifier si l'utilisateur demande un téléchargement
      if (isDownloadRequest(text)) {
        await handleDownloadRequest(fullPrompt);
      } else if (isGuidedPrompt && conversationState.step === 'initial') {
        // Pour les prompts guidés : génération directe sans analyse
        await handleContentGenerationWithSessionId(
          fullPrompt,
          null, // Pas de sessionId pour le premier message
          documentType,
          subType
        );
        // Réinitialiser le flag après traitement
        setIsGuidedPrompt(false);
      } else if (conversationState.step === 'initial') {
        // Première étape : Analyser le prompt (mode manuel uniquement)
        await handlePromptAnalysis(fullPrompt);
      } else {
        // Étapes suivantes : Générer ou améliorer le contenu
        await handleContentGeneration(fullPrompt);
      }

    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      showError(`Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      
      const errorMsg: ContentGenerationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "Désolé, une erreur s'est produite. Veuillez réessayer.",
        timestamp: new Date(),
        type: 'error'
      };
      setMessages(prev => [...prev, errorMsg]);
      
      setConversationState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Erreur inconnue',
        isGenerating: false 
      }));
    } finally {
      setSending(false);
    }
  };

  // Fonction pour vérifier la disponibilité du modèle
  const checkModelAvailability = async (subtypeId: number) => {
    try {
      // On utilise analyzePrompt avec un prompt minimal pour vérifier la disponibilité
      const testAnalysis = await contentGenerationService.analyzePrompt(
        "test",
        documentType || category,
        subType,
        subtypeId
      );
      
      // Si model_available est false ou message est "Bientôt disponible", le modèle n'est pas disponible
      const available = testAnalysis.model_available !== false && testAnalysis.message !== "Bientôt disponible";
      setModelAvailable(available);
    } catch (error) {
      console.error('Erreur lors de la vérification de disponibilité:', error);
      // En cas d'erreur, on assume que le modèle n'est pas disponible pour être sûr
      setModelAvailable(false);
    }
  };

  const handlePromptAnalysis = async (prompt: string) => {
    try {
      console.log('🔍 Analyse du prompt:', prompt);
      
      const analysis = await contentGenerationService.analyzePrompt(
        prompt,
        documentType,
        subType,
        documentSubtypeId
      );

      // Vérifier si le modèle n'est pas disponible
      if (analysis.model_available === false || analysis.message === "Bientôt disponible") {
        const unavailableMsg: ContentGenerationMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `⏳ **Bientôt disponible**\n\nCe document clé sera bientôt disponible. Le modèle de référence est en cours de préparation.`,
          timestamp: new Date(),
          type: 'error',
          metadata: {
            sessionId: analysis.session_id || undefined,
            documentType: analysis.document_type,
            canDownload: false
          }
        };
        setMessages(prev => [...prev, unavailableMsg]);
        setConversationState(prev => ({ ...prev, isGenerating: false }));
        showError("Ce document clé sera bientôt disponible");
        return;
      }

      // Mettre à jour l'état de conversation
      setConversationState(prev => ({
        ...prev,
        step: 'suggestion',
        sessionId: analysis.session_id,
        originalPrompt: analysis.original_prompt,
        suggestedPrompt: analysis.suggested_prompt,
        documentType: analysis.document_type,
        documentSubtype: analysis.document_subtype,
        isGenerating: false
      }));

      // Construire le contenu du message avec suggestions alternatives
      let content = "";
      if (analysis.skip_improvement) {
        content = `**${analysis.analysis}**\n\nVeuillez fournir plus de détails sur le document que vous souhaitez générer.`;
      } else {
        content = `**${analysis.analysis}**\n\n**Suggestion principale :**\n${analysis.suggested_prompt}`;
        
        // Ajouter les suggestions alternatives si disponibles
        if (analysis.alternative_suggestions && analysis.alternative_suggestions.length > 0) {
          content += `\n\n**Autres suggestions :**\n`;
          analysis.alternative_suggestions.forEach((suggestion, index) => {
            content += `\n**${index + 2}.** ${suggestion}`;
          });
        }
      }
      
      if (analysis.skip_improvement) {
        // Si l'utilisateur refuse l'amélioration ou si c'est une erreur
        if (analysis.analysis.includes("Prompt accepté tel quel")) {
          // L'utilisateur a refusé l'amélioration, passer directement à la génération
          setConversationState(prev => ({ 
            ...prev, 
            step: 'generating',
            sessionId: analysis.session_id 
          }));
          
          const infoMsg: ContentGenerationMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: `✅ **Prompt accepté !**\n\nVotre prompt a été accepté tel quel. Génération du document en cours...`,
            timestamp: new Date(),
            type: 'content',
            metadata: {
              sessionId: analysis.session_id,
              documentType: analysis.document_type,
              canDownload: false,
              skipImprovement: true
            }
          };
          setMessages(prev => [...prev, infoMsg]);
          
          // Générer directement le contenu avec le sessionId de l'analyse
          await handleContentGenerationWithSessionId(
            analysis.original_prompt, 
            analysis.session_id,
            analysis.document_type,
            analysis.document_subtype
          );
        } else {
          // C'est une erreur (prompt trop vague)
          const errorMsg: ContentGenerationMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: content,
            timestamp: new Date(),
            type: 'error',
            metadata: {
              sessionId: analysis.session_id,
              documentType: analysis.document_type,
              canDownload: false,
              skipImprovement: true
            }
          };
          setMessages(prev => [...prev, errorMsg]);
          showError("Prompt trop vague. Veuillez fournir plus de détails.");
        }
      } else {
        // Si c'est une amélioration, remplir le champ de saisie avec le prompt amélioré
        setChatInput(analysis.suggested_prompt);
        
        // Mettre à jour l'état de conversation avec le sessionId
        setConversationState(prev => ({ 
          ...prev, 
          step: 'suggestion',
          sessionId: analysis.session_id 
        }));
        
        // Ajouter un message informatif
        const infoMsg: ContentGenerationMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `💡 **Prompt amélioré !**\n\nJ'ai amélioré votre prompt pour le rendre plus précis et détaillé. Vous pouvez le modifier si nécessaire avant de le soumettre.`,
          timestamp: new Date(),
          type: 'content',
          metadata: {
            sessionId: analysis.session_id,
            documentType: analysis.document_type,
            canDownload: false,
            skipImprovement: false
          }
        };
        setMessages(prev => [...prev, infoMsg]);
        showSuccess("Prompt amélioré ! Modifiez-le si nécessaire et soumettez-le.");
      }

    } catch (error) {
      console.error('Erreur lors de l\'analyse:', error);
      throw error;
    }
  };

  const handleContentGenerationWithSessionId = async (prompt: string, sessionId: number | null, documentType: string, documentSubtype: string) => {
    try {
      console.log('📄 Génération de contenu avec sessionId:', prompt, sessionId, documentType, documentSubtype);
      
      // Si pas de sessionId, créer une nouvelle session d'abord
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const analysis = await contentGenerationService.analyzePrompt(
          prompt,
          documentType,
          documentSubtype,
          documentSubtypeId
        );
        
        // Vérifier si le modèle n'est pas disponible
        if (analysis.model_available === false || analysis.message === "Bientôt disponible") {
          const unavailableMsg: ContentGenerationMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: `⏳ **Bientôt disponible**\n\nCe document clé sera bientôt disponible. Le modèle de référence est en cours de préparation.`,
            timestamp: new Date(),
            type: 'error',
            metadata: {
              sessionId: analysis.session_id || undefined,
              documentType: analysis.document_type,
              canDownload: false
            }
          };
          setMessages(prev => [...prev, unavailableMsg]);
          setConversationState(prev => ({ ...prev, isGenerating: false }));
          showError("Ce document clé sera bientôt disponible");
          return;
        }
        
        currentSessionId = analysis.session_id;
      }
      
      const content = await contentGenerationService.generateContent(
        currentSessionId,
        prompt,
        documentType,
        documentSubtype,
        false, // Pour l'instant, on n'accepte pas automatiquement la suggestion
        documentSubtypeId
      );
      
      // Vérifier si le modèle n'est pas disponible dans la réponse de génération
      if (content.metadata?.model_available === false || content.metadata?.message === "Bientôt disponible" || content.error) {
        const unavailableMsg: ContentGenerationMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `⏳ **Bientôt disponible**\n\nCe document clé sera bientôt disponible. Le modèle de référence est en cours de préparation.`,
          timestamp: new Date(),
          type: 'error',
          metadata: {
            sessionId: content.session_id,
            documentType: content.metadata.document_type,
            canDownload: false
          }
        };
        setMessages(prev => [...prev, unavailableMsg]);
        setConversationState(prev => ({ ...prev, isGenerating: false }));
        showError("Ce document clé sera bientôt disponible");
        return;
      }

      // Mettre à jour l'état de conversation
      const previousContent = conversationState.generatedContent || "";
      const isImprovement = previousContent.length > 0;
      
      setConversationState(prev => ({
        ...prev,
        step: 'completed',
        sessionId: currentSessionId,
        documentType: documentType,
        documentSubtype: documentSubtype,
        generatedContent: content.content,
        canDownload: true,
        isGenerating: false
      }));

      // Ajouter le message de contenu généré
      const contentMsg: ContentGenerationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: content.content,
        timestamp: new Date(),
        type: 'content',
        metadata: {
          sessionId: currentSessionId,
          documentType: documentType,
          canDownload: false
        }
      };
      setMessages(prev => [...prev, contentMsg]);
      
      const successMessage = isImprovement 
        ? "Document amélioré avec succès !" 
        : "Document généré avec succès !";
      showSuccess(successMessage);
      
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      showError("Erreur lors de la génération du document.");
      throw error;
    }
  };

  const handleContentGeneration = async (prompt: string) => {
    try {
      console.log('📄 Génération de contenu:', prompt);
      
      if (!conversationState.sessionId) {
        throw new Error('Session ID manquant');
      }

      const content = await contentGenerationService.generateContent(
        conversationState.sessionId,
        prompt,
        conversationState.documentType,
        conversationState.documentSubtype,
        false, // Pour l'instant, on n'accepte pas automatiquement la suggestion
        documentSubtypeId
      );
      
      // Vérifier si le modèle n'est pas disponible
      if (content.metadata?.model_available === false || content.metadata?.message === "Bientôt disponible" || content.error) {
        const unavailableMsg: ContentGenerationMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `⏳ **Bientôt disponible**\n\nCe document clé sera bientôt disponible. Le modèle de référence est en cours de préparation.`,
          timestamp: new Date(),
          type: 'error',
          metadata: {
            sessionId: content.session_id,
            documentType: content.metadata.document_type,
            canDownload: false
          }
        };
        setMessages(prev => [...prev, unavailableMsg]);
        setConversationState(prev => ({ ...prev, isGenerating: false }));
        showError("Ce document clé sera bientôt disponible");
        return;
      }

      // Mettre à jour l'état de conversation
      setConversationState(prev => ({
        ...prev,
        step: 'completed',
        generatedContent: content.content,
        canDownload: true,
        isGenerating: false
      }));

      // Déterminer si c'est une amélioration ou une première génération
      const isImprovement = conversationState.generatedContent && conversationState.generatedContent.length > 0;
      const successMessage = isImprovement 
        ? "Document amélioré avec succès !" 
        : "Contenu généré avec succès !";

      // Ajouter le message de contenu généré
      const contentMsg: ContentGenerationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: content.content,
        timestamp: new Date(),
        type: 'content',
        metadata: {
          sessionId: content.session_id,
          documentType: conversationState.documentType,
          canDownload: false
        }
      };

      setMessages(prev => [...prev, contentMsg]);
      showSuccess(successMessage);

    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      throw error;
    }
  };


  const isDownloadRequest = (text: string): boolean => {
    const downloadKeywords = [
      'télécharger', 'telecharger', 'download', 'télécharge', 'telecharge',
      'fichier word', 'document word', 'fichier docx', 'document docx',
      'générer fichier', 'generer fichier', 'créer fichier', 'creer fichier',
      'exporter', 'export', 'sauvegarder', 'sauvegarder en fichier'
    ];
    
    const lowerText = text.toLowerCase();
    return downloadKeywords.some(keyword => lowerText.includes(keyword));
  };

  const handleDownloadRequest = async (request: string) => {
    // Trouver le dernier message de contenu généré par l'IA
    const lastContentMessage = messages
      .filter(msg => msg.role === 'assistant' && msg.type === 'content')
      .pop();

    if (!lastContentMessage) {
      const errorMsg: ContentGenerationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "Je ne peux pas générer de fichier car aucun contenu n'a encore été généré. Veuillez d'abord générer un document.",
        timestamp: new Date(),
        type: 'error'
      };
      setMessages(prev => [...prev, errorMsg]);
      return;
    }

    try {
      console.log('📄 Téléchargement du document avec le contenu exact de la conversation');
      console.log('Contenu à télécharger:', lastContentMessage.content.substring(0, 200) + '...');
      
      // Appeler l'API pour générer le fichier Word avec le contenu exact de la conversation
      // documentType doit être le type de document (ex: "Stratégiques"), pas le nom du document clé
      // documentSubtype doit être le nom du document clé (ex: "Stratégie nationale de vaccination...")
      const docType = conversationState.documentType || documentType || category;
      const docSubtype = conversationState.documentSubtype || subType;
      
      const response = await contentGenerationService.downloadDocument(
        conversationState.sessionId!,
        lastContentMessage.content, // Utiliser le contenu exact du message
        docType, // Type de document (ex: "Stratégiques")
        docSubtype // Nom du document clé (ex: "Stratégie nationale de vaccination...")
      );

      // Créer un blob et générer un lien
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = URL.createObjectURL(blob);
      const fileName = `${conversationState.documentType || documentType}_${conversationState.documentSubtype || subType}_${Date.now()}.docx`;

      // Ajouter un message avec le fichier téléchargeable
      const fileMsg: ContentGenerationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `📄 **Document Word généré avec succès !**\n\nVotre document "${conversationState.documentType || documentType}" est prêt. Cliquez sur le bouton ci-dessous pour le télécharger.`,
        timestamp: new Date(),
        type: 'content',
        metadata: {
          sessionId: conversationState.sessionId || undefined,
          documentType: conversationState.documentType || documentType,
          canDownload: false,
          fileUrl: url,
          fileName: fileName,
          fileSize: blob.size
        }
      };
      setMessages(prev => [...prev, fileMsg]);
      
      showSuccess("Document Word généré avec succès !");
    } catch (error) {
      console.error('Erreur lors de la génération du fichier:', error);
      
      // Extraire le message d'erreur détaillé
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      const detailedError = errorMessage.includes('Erreur') ? errorMessage : `Erreur: ${errorMessage}`;
      
      const errorMsg: ContentGenerationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `❌ **Erreur lors de la génération du fichier Word**\n\n${detailedError}\n\nVeuillez vérifier que le contenu a bien été généré et réessayer.`,
        timestamp: new Date(),
        type: 'error'
      };
      setMessages(prev => [...prev, errorMsg]);
      
      showError(detailedError);
    }
  };


  /* ─────────────────────── History Sidebar ────────────────────── */
  const HistorySidebar: React.FC<{
    conversations: Conversation[];
    currentId: string | null;
    pinnedIds: Set<string>;
    onSelect: (id: string) => void;
    onTogglePin: (id: string) => void;
    onDelete: (id: string) => void;
    collapsed: boolean;
    onToggleCollapse: () => void;
    editingTitleId: string | null;
    editingTitle: string;
    onStartEdit: (id: string, title: string) => void;
    onSaveEdit: (id: string) => void;
    onCancelEdit: () => void;
    onTitleChange: (title: string) => void;
  }> = ({ conversations, currentId, pinnedIds, onSelect, onTogglePin, onDelete, collapsed, onToggleCollapse, editingTitleId, editingTitle, onStartEdit, onSaveEdit, onCancelEdit, onTitleChange }) => {
    const sortedConversations = [...conversations].sort((a, b) => {
      const aPinned = pinnedIds.has(a.id);
      const bPinned = pinnedIds.has(b.id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      return b.createdAt.getTime() - a.createdAt.getTime();
    });

    return (
      <motion.div 
        className={`border-r bg-white/50 backdrop-blur-sm transition-all duration-300 ${
          collapsed ? 'w-12' : 'w-80'
        }`}
        animate={{ width: collapsed ? 48 : 320 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <div className="p-4 border-b flex items-center justify-between">
          {!collapsed && (
            <motion.h3 
              className="font-semibold text-gray-900"
              initial={{ opacity: 1 }}
              animate={{ opacity: collapsed ? 0 : 1 }}
              transition={{ duration: 0.2 }}
            >
              Historique des conversations
            </motion.h3>
          )}
          <motion.button
            onClick={onToggleCollapse}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={collapsed ? "Afficher le sidebar" : "Masquer le sidebar"}
          >
            {collapsed ? (
              <PanelLeftOpen className="w-4 h-4 text-gray-600 group-hover:text-gray-800" />
            ) : (
              <PanelLeftClose className="w-4 h-4 text-gray-600 group-hover:text-gray-800" />
            )}
          </motion.button>
        </div>
        <AnimatePresence>
          {!collapsed && (
            <motion.div 
              className="overflow-y-auto h-full"
              initial={{ opacity: 1, width: "100%" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
            >
              {sortedConversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <History className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Aucune conversation</p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {sortedConversations.map((conv) => (
                    <motion.div
                      key={conv.id}
                      whileHover={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        currentId === conv.id 
                          ? conv.isGuided 
                            ? "bg-blue-50 border border-blue-200" 
                            : "bg-teal-50 border border-teal-200"
                          : conv.isGuided 
                            ? "hover:bg-blue-50" 
                            : "hover:bg-gray-50"
                      }`}
                      onClick={() => onSelect(conv.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {editingTitleId === conv.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={editingTitle}
                                onChange={(e) => onTitleChange(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    onSaveEdit(conv.id);
                                  } else if (e.key === 'Escape') {
                                    onCancelEdit();
                                  }
                                }}
                                className="text-sm font-medium text-gray-900 bg-white border border-teal-300 rounded px-2 py-1 w-full"
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSaveEdit(conv.id);
                                }}
                                className="p-1 hover:bg-teal-100 rounded text-teal-600"
                                title="Sauvegarder"
                              >
                                <Check className="w-3 h-3" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelEdit();
                                }}
                                className="p-1 hover:bg-gray-100 rounded text-gray-500"
                                title="Annuler"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div 
                              className="group"
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                onStartEdit(conv.id, conv.title);
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium truncate group-hover:${
                                  conv.isGuided ? "text-blue-700" : "text-teal-700"
                                } ${
                                  conv.isGuided ? "text-blue-900" : "text-gray-900"
                                }`}>
                                  {conv.title}
                                </p>
                                {conv.isGuided && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full" title="Conversation guidée"></div>
                                )}
                              </div>
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {conv.messages.length} message{conv.messages.length > 1 ? "s" : ""}
                          </p>
                          <p className="text-xs text-gray-400">
                            {conv.createdAt.toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onTogglePin(conv.id);
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                            title={pinnedIds.has(conv.id) ? "Désépingler" : "Épingler"}
                          >
                            {pinnedIds.has(conv.id) ? (
                              <Pin className="w-3 h-3 text-teal-600" />
                            ) : (
                              <PinOff className="w-3 h-3 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(conv.id);
                            }}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Supprimer"
                          >
                            <X className="w-3 h-3 text-gray-400 hover:text-red-600" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  /* ───────────────────────── UI Blocks ──────────────────────── */
  const HeaderHero = (
    <motion.div
      {...fadeUp(0)}
      className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-slate-50 to-blue-50 p-4"
    >
      {/* Parallax blob */}
      <motion.div
        className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-teal-300/20 blur-3xl"
        style={{ y: blobY, scale: blobScale }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: EASE_OUT }}
        aria-hidden
      />

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <motion.h2
            className="text-3xl font-extrabold text-center md:text-left"
            {...fadeUpInView(0.02)}
          >
            Rédaction de Documents
          </motion.h2>
          <motion.p
            className="text-center md:text-left text-gray-600 max-w-2xl mt-1 text-sm"
            {...fadeUpInView(0.06)}
          >
            Génération assistée, conformité aux standards, ergonomie soignée, et animations douces respectant les préférences d’accessibilité.
          </motion.p>
        </div>
        <motion.div
          className="hidden md:flex items-center gap-2"
          variants={staggerList}
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, amount: 0.25 }}
        >
          <motion.span
            className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm text-gray-700"
            whileHover={hoverElevate}
            whileTap={tapPress}
          >
            <ShieldCheck className="w-4 h-4 text-emerald-600" aria-hidden />
            Plus de 10 en un Outil
          </motion.span>
          <motion.span
            className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm text-gray-700"
            whileHover={hoverElevate}
            whileTap={tapPress}
          >
            <Info className="w-4 h-4 text-teal-600" aria-hidden />
            Plus à venir
          </motion.span>
        </motion.div>
      </div>

        <div className="mt-3 flex justify-center md:justify-start">
        <Segmented value={tab} onChange={handleTabChange} />
      </div>
    </motion.div>
  );

  const EmptyState = (
    <motion.div
      {...fadeUp(0.05)}
      className="h-full grid place-items-center text-gray-400"
      role="status"
      aria-live="polite"
    >
      <div className="text-center">
        <motion.div
          className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 1.4, ease: EASE_OUT, repeat: Infinity }}
        >
          <Sparkles aria-hidden />
        </motion.div>
        <p className="font-medium">Aucune discussion</p>
        <p className="text-sm">Sélectionnez un type de document et commencez à écrire.</p>
      </div>
    </motion.div>
  );

  const ManualPanel = (
    <motion.section
      id="panel-manual"
      role="tabpanel"
      aria-labelledby="tab-manual"
      className="bg-white rounded-2xl shadow border p-4"
    >
      {/* Header: Doc type selector + actions */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="w-full md:w-auto">
          <motion.div whileHover={hoverElevate} transition={{ type: "tween", duration: 0.15 }}>
            <DocumentType
              category={category}
              setCategory={(cat) => setCategory(cat)}
              subType={subType}
              setSubType={(st) => {
                setSubType(st);
                // Ne pas modifier documentType ici, il doit rester le type de document (category)
              }}
              onChange={({ category: newCat, subType: newSub, domain, domainId, documentTypeId, documentSubtypeId }) => {
                setCategory(newCat || "");
                setSubType(newSub || "");
                // documentType doit contenir le type de document (ex: "Stratégiques"), pas le sous-type
                // On garde category qui est le type de document
                setDocumentType(newCat || "");
                // Stocker le documentSubtypeId pour l'utiliser dans les appels API
                setDocumentSubtypeId(documentSubtypeId);
                
                // Vérifier la disponibilité du modèle si un documentSubtypeId est sélectionné
                if (documentSubtypeId) {
                  checkModelAvailability(documentSubtypeId);
                } else {
                  // Si aucun document clé n'est sélectionné, le modèle est considéré comme disponible
                  setModelAvailable(true);
                }
              }}
            />
          </motion.div>
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            onClick={() => startNewConversation(false)}
            whileHover={hoverElevate}
            whileTap={tapPress}
            className="inline-flex items-center gap-2 rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-blue-600"
            title="Nouveau chat"
          >
            <Plus className="w-4 h-4" aria-hidden />
            Nouveau
          </motion.button>
          <motion.button
            type="button"
            whileHover={hoverElevate}
            whileTap={tapPress}
            className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-600"
            title="Aide"
          >
            <HelpCircle className="w-4 h-4 text-teal-600" aria-hidden />
            Aide
          </motion.button>
        </div>
      </div>

      {/* Body: Sidebar + Chat */}
      <div className="relative flex h-[380px] md:h-[420px] rounded-xl border bg-gray-50/60">
        {/* Sidebar (history) */}
        <HistorySidebar
          conversations={conversations}
          currentId={currentConversationId}
          pinnedIds={pinnedIds}
          onSelect={(id) => {
            const conv = conversations.find(c => c.id === id);
            if (conv) {
              setCurrentConversationId(id);
              // Convertir les messages si nécessaire
              const convertedMessages = conv.messages.map(msg => ({
                id: msg.id || crypto.randomUUID(),
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp || new Date(),
                type: msg.type || 'prompt',
                metadata: msg.metadata
              }));
              setMessages(convertedMessages);
              // Restaurer le champ de saisie si disponible
              setChatInput(conv.chatInput || "");
              
              // Réinitialiser le prompt guidé si on change de conversation
              if (!conv.isGuided) {
                setGuidedPrompt("");
                setIsGuidedPrompt(false);
              } else {
                // Si c'est une conversation guidée, restaurer le prompt guidé
                setGuidedPrompt(conv.chatInput || "");
                setIsGuidedPrompt(true);
              }
              // Restaurer le type de document si disponible
              // conv.docType contient le type de document (ex: "Stratégiques")
              // conv.docSubtype contient le nom du document clé (ex: "Stratégie nationale...")
              
              // Réinitialiser la disponibilité du modèle (sera vérifiée si un document clé est sélectionné)
              setModelAvailable(true);
              
              if (conv.docType) {
                setDocumentType(conv.docType);
                setCategory(conv.docType);
              }
              if (conv.docSubtype) {
                setSubType(conv.docSubtype);
              }
            }
          }}
          onTogglePin={togglePin}
          onDelete={deleteConversation}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          editingTitleId={editingTitleId}
          editingTitle={editingTitle}
          onStartEdit={startEditingTitle}
          onSaveEdit={saveTitleEdit}
          onCancelEdit={cancelTitleEdit}
          onTitleChange={setEditingTitle}
        />

        {/* Chat area */}
        <div className="relative flex-1">
          {/* Top indeterminate progress when sending */}
          <AnimatePresence>
            {sending && (
              <motion.div
                key="progress"
                className="absolute left-0 right-0 top-0 h-0.5 bg-transparent overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="h-full bg-teal-600"
                  style={{ width: "30%" }}
                  initial={{ x: "-30%" }}
                  animate={{ x: ["-30%", "110%"] }}
                  transition={{ duration: 1.2, ease: EASE_OUT, repeat: Infinity }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            className="absolute inset-0 space-y-2 overflow-y-auto p-3 pb-20"
            role="log"
            aria-live="polite"
            aria-busy={sending}
            variants={staggerList}
            initial="initial"
            animate="animate"
          >
            {messages.length === 0 ? (
              EmptyState
            ) : (
              <AnimatePresence initial={false}>
                {messages.map((m, i) => (
                  <motion.div key={m.id} {...fadeUp(0.02 + i * 0.02)}>
                    <ChatBubble role={m.role} text={m.content} />
                    
                    {/* Affichage du fichier téléchargeable */}
                    {m.metadata?.fileUrl && m.metadata?.fileName && (
                      <motion.div 
                        className="mt-3"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <FileAttachment
                          fileName={m.metadata.fileName}
                          fileUrl={m.metadata.fileUrl}
                          fileSize={m.metadata.fileSize}
                        />
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            )}

            {/* Skeleton while sending */}
            {sending && messages.length > 0 && (
              <div className="animate-pulse space-y-2">
                <div className="h-4 w-48 rounded bg-gray-200/80" />
                <div className="h-4 w-72 rounded bg-gray-200/70" />
                <div className="h-4 w-64 rounded bg-gray-200/60" />
              </div>
            )}
            <div ref={bottomRef} />
          </motion.div>

          {/* Composer with subtle hover */}
          <motion.div
            className="absolute left-0 right-0 bottom-0 border-t bg-white/95 p-2 backdrop-blur"
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { duration: 0.25, ease: EASE_OUT } }}
          >
            <motion.div whileHover={{ y: -1 }} transition={{ type: "tween", duration: 0.15 }}>
              {!modelAvailable && subType ? (
                // Afficher le message "Bientôt disponible" si le modèle n'est pas disponible
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">⏳</span>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-700 mb-1">⏳ **Bientôt disponible**</p>
                      <p className="text-sm text-gray-600">
                        Ce document clé sera bientôt disponible. Le modèle de référence est en cours de préparation.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <ChatComposer
                  value={chatInput}
                  onChange={setChatInput}
                  onSend={handleSendMessage}
                  attachments={attachments}
                  onRemoveAttachment={(i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                  onAttach={(files) => {
                    const MAX = 5;
                    const room = Math.max(0, MAX - attachments.length);
                    const toAdd = files.slice(0, room);
                    if (toAdd.length > 0) setAttachments((prev) => [...prev, ...toAdd]);
                  }}
                  disabled={sending || !documentType || conversationState.isGenerating || !modelAvailable}
                  placeholder={
                    !modelAvailable && subType
                      ? "⏳ Ce document clé sera bientôt disponible..."
                      : conversationState.step === 'initial' 
                        ? "Décrivez le document que vous souhaitez générer… (Ctrl/Cmd+Entrée pour envoyer)"
                        : "Modifiez le prompt si nécessaire et soumettez-le… (Ctrl/Cmd+Entrée pour envoyer)"
                  }
                />
              )}
            </motion.div>
            {!documentType && (
              <p className="mt-2 text-xs text-red-600">Sélectionnez un type de document pour activer l'envoi.</p>
            )}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );

  const GuidedPanel = (
    <motion.section
      id="panel-guided"
      role="tabpanel"
      aria-labelledby="tab-guided"
      className="bg-white rounded-2xl shadow border p-4"
    >
      <GuidedPromptOptions
        category={category}
        setCategory={(cat) => setCategory(cat)}
        subType={subType}
        setSubType={(st) => {
          setSubType(st);
          // Ne pas modifier documentType ici, il doit rester le type de document (category)
        }}
        onDocTypeChange={({ category: newCat, subType: newSub, documentSubtypeId: newSubtypeId }) => {
          setCategory(newCat);
          setSubType(newSub);
          // documentType doit contenir le type de document (ex: "Stratégiques"), pas le sous-type
          setDocumentType(newCat || "");
          setDocumentSubtypeId(newSubtypeId);
          
          // Vérifier la disponibilité du modèle si un documentSubtypeId est sélectionné
          if (newSubtypeId) {
            checkModelAvailability(newSubtypeId);
          } else {
            // Si aucun document clé n'est sélectionné, le modèle est considéré comme disponible
            setModelAvailable(true);
          }
        }}
        objective={objective}
        setObjective={setObjective}
        targetAudience={targetAudience}
        setTargetAudience={setTargetAudience}
        sections={sections}
        setSections={setSections}
        tone={tone}
        setTone={setTone}
        onGenerate={handleGenerateFromGuided}
      />
    </motion.section>
  );



  /* ───────────────────────── render ─────────────────────────── */
  return (
    <DashboardLayout>
      {/* Aurora Background Effect */}
      
      <main className="space-y-3 content-above-aurora">
        {HeaderHero}

        <div className="sr-only" id="page-help">
          Cette interface respecte les préférences de mouvement du système, offre des contrastes suffisants et des indications de focus visibles.
        </div>

        {/* single keyed wrapper = no ghost panels, clean switch */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ 
              opacity: 1, 
              y: 0,
              transition: { 
                duration: 0.3, 
                ease: EASE_OUT,
                delay: 0.1
              } 
            }}
            exit={{ 
              opacity: 0, 
              y: 8, 
              transition: { 
                duration: 0.2, 
                ease: EASE_IN 
              } 
            }}
          >
            {tab === "manual" ? ManualPanel : GuidedPanel}
          </motion.div>
        </AnimatePresence>
      </main>

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </DashboardLayout>
  );
};

export default RedactionPage;
